import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";

import useNotification from "../../components/notifications/useNotifications";
import AwaitingDismantlingPanel from "../../components/productionPlanner/AwaitingDismantlingPanel";
import BayDropZone from "../../components/productionPlanner/BayDropZone";
import PlannerToolbar from "../../components/productionPlanner/PlannerToolbar";
import ProductionLineDayColumn from "../../components/productionPlanner/ProductionLineDayColumn";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDismantlingBays from "../../data/useDismantlingBays";
import useDismantlingBaySetting from "../../data/useDismantlingBaySetting";
import useProductionPlanner from "../../data/useProductionPlanner";
import useApiHelper from "../../hooks/useApiHelper";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import { DismantlingBayType, IDismantlingBay, IDismantlingBaySetting } from "../../models/DismantlingBay";
import {
  IPlannerDragItem,
  IPlannerVehicleRow,
  PlannerColourBy,
  PlannerComplexityFilter,
  PlannerDayRange,
  PlannerSort,
  PlannerView,
} from "../../models/ProductionPlanner";
import { FinancialDecision, ISingleVehicleResponse } from "../../models/Vehicle";
import printHtml from "../../utils/printHtml";

const allocationDateKey = (iso: string | null): string | null => {
  if (!iso) return null;
  return DateTime.fromISO(iso, { zone: "utc" }).toISODate();
};

const STORAGE_PREFIX = "productionPlanner";
const STORAGE_KEY = {
  view: `${STORAGE_PREFIX}.view`,
  sort: `${STORAGE_PREFIX}.sort`,
  complexityFilter: `${STORAGE_PREFIX}.complexityFilter`,
  colourBy: `${STORAGE_PREFIX}.colourBy`,
  dayRange: `${STORAGE_PREFIX}.dayRange`,
};

const readStored = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(key);
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
};

const readStoredDayRange = (fallback: PlannerDayRange): PlannerDayRange => {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(STORAGE_KEY.dayRange);
  const allowed: PlannerDayRange[] = [1, 3, 5, 7];
  const parsed = value !== null ? Number(value) : NaN;
  return allowed.includes(parsed as PlannerDayRange) ? (parsed as PlannerDayRange) : fallback;
};

const writeStored = (key: string, value: string | number) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, String(value));
};

export default function ProductionPlanner() {
  const { setInfo } = usePageNameContext();
  const isTme = CheckFeatureFlag("TME");
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Allocation");
  const { generateVehicleLabel } = useLabelGenerator();
  const { addNotification } = useNotification();
  const { get } = useApiHelper();

  const [view, setView] = useState<PlannerView>(() =>
    readStored<PlannerView>(STORAGE_KEY.view, ["Bays", "ProductionLine"] as const, "ProductionLine")
  );
  const [sort, setSort] = useState<PlannerSort>(() =>
    readStored<PlannerSort>(
      STORAGE_KEY.sort,
      ["Oldest", "Newest", "Easiest", "Hardest"] as const,
      "Oldest"
    )
  );
  const [complexityFilter, setComplexityFilter] = useState<PlannerComplexityFilter>(() =>
    readStored<PlannerComplexityFilter>(
      STORAGE_KEY.complexityFilter,
      ["All", "Easy", "Medium", "Hard"] as const,
      "All"
    )
  );
  const [colourBy, setColourBy] = useState<PlannerColourBy>(() =>
    readStored<PlannerColourBy>(STORAGE_KEY.colourBy, ["Complexity", "Age"] as const, "Complexity")
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [dayRange, setDayRange] = useState<PlannerDayRange>(() => readStoredDayRange(3));
  const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.local().startOf("day"));
  const [clearOpen, setClearOpen] = useState<boolean>(false);
  const [isClearingAll, setIsClearingAll] = useState<boolean>(false);
  const [isPrintingLabels, setIsPrintingLabels] = useState<boolean>(false);

  const fromDate = selectedDate;
  const toDate = view === "Bays" ? selectedDate : selectedDate.plus({ days: dayRange - 1 });
  const fromIso = fromDate.toISODate() ?? "";
  const toIso = toDate.toISODate() ?? "";

  const { dismantlingBays } = useDismantlingBays();
  const { awaiting, allocated, allocateVehicle, clearAllocation, clearDay, reorderBay } =
    useProductionPlanner({
      from: fromIso,
      to: toIso,
    });
  const { settingsRange } = useDismantlingBaySetting({ from: fromIso, to: toIso });

  useEffect(() => {
    setInfo({
      name: "Production Planner",
      desc: "Plan the dismantling production line by allocating vehicles across the coming days",
    });
  }, [setInfo]);

  useEffect(() => writeStored(STORAGE_KEY.view, view), [view]);
  useEffect(() => writeStored(STORAGE_KEY.sort, sort), [sort]);
  useEffect(() => writeStored(STORAGE_KEY.complexityFilter, complexityFilter), [complexityFilter]);
  useEffect(() => writeStored(STORAGE_KEY.colourBy, colourBy), [colourBy]);
  useEffect(() => writeStored(STORAGE_KEY.dayRange, dayRange), [dayRange]);

  const bays: IDismantlingBay[] = useMemo(() => {
    return [...(dismantlingBays.data?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  }, [dismantlingBays.data]);

  const bayViewBays = useMemo(
    () => bays.filter((b) => b.type === DismantlingBayType.Bay),
    [bays]
  );

  const productionLineBays = useMemo(
    () => bays.filter((b) => b.type === DismantlingBayType.ProductionLine),
    [bays]
  );

  const allocatedRows: IPlannerVehicleRow[] = allocated.data?.data ?? [];
  // With TME enabled, only vehicles marked Purchased are awaiting dismantling.
  const awaitingRows: IPlannerVehicleRow[] = useMemo(() => {
    const rows = awaiting.data?.data ?? [];
    return isTme ? rows.filter((v) => v.financialDecision === FinancialDecision.Purchased) : rows;
  }, [awaiting.data, isTme]);

  const allocatedByBayDate = useMemo(() => {
    const map = new Map<string, IPlannerVehicleRow[]>();
    allocatedRows.forEach((v) => {
      const dateKey = allocationDateKey(v.plannedDismantlingOn);
      if (!v.dismantlingBayId || !dateKey) return;
      const key = `${v.dismantlingBayId}|${dateKey}`;
      const list = map.get(key) ?? [];
      list.push(v);
      map.set(key, list);
    });
    return map;
  }, [allocatedRows]);

  const vehiclesFor = (bayId: string, dateIso: string): IPlannerVehicleRow[] =>
    allocatedByBayDate.get(`${bayId}|${dateIso}`) ?? [];

  const settingsByBayDate = useMemo(() => {
    const map = new Map<string, IDismantlingBaySetting>();
    (settingsRange.data?.data ?? []).forEach((s) => {
      const dateKey = allocationDateKey(s.date);
      if (!dateKey) return;
      map.set(`${s.dismantlingBayId}|${dateKey}`, s);
    });
    return map;
  }, [settingsRange.data]);

  const settingFor = (bayId: string, dateIso: string): IDismantlingBaySetting | undefined =>
    settingsByBayDate.get(`${bayId}|${dateIso}`);

  const handleDrop = (item: IPlannerDragItem, bayId: string, dateIso: string) => {
    if (item.currentBayId === bayId && allocationDateKey(item.currentPlannedOn) === dateIso) return;

    allocateVehicle.mutate(
      {
        vehicleId: item.vehicleId,
        body: { dismantlingBayId: bayId, plannedOn: dateIso },
      },
      {
        onSuccess: saveHandlers.onSuccess,
        onError: saveHandlers.onError,
      }
    );
  };

  const handleReorder = (bayId: string, dateIso: string, vehicleIds: string[]) => {
    reorderBay.mutate(
      { dismantlingBayId: bayId, plannedOn: dateIso, vehicleIds },
      {
        onSuccess: saveHandlers.onSuccess,
        onError: saveHandlers.onError,
      }
    );
  };

  const handleDropToAwaiting = (item: IPlannerDragItem) => {
    if (!item.currentBayId) return;

    clearAllocation.mutate(item.vehicleId, {
      onSuccess: deleteHandlers.onSuccess,
      onError: deleteHandlers.onError,
    });
  };

  const handlePrintAll = async () => {
    if (isPrintingLabels) return;

    const visibleBayIds = new Set(
      (view === "Bays" ? bayViewBays : productionLineBays).map((b) => b.id)
    );
    const printableVehicles = allocatedRows.filter(
      (v) => v.dismantlingBayId && visibleBayIds.has(v.dismantlingBayId)
    );

    if (printableVehicles.length === 0) {
      addNotification({
        variant: "warn",
        primaryText: "No vehicles allocated in the current view",
      });
      return;
    }

    setIsPrintingLabels(true);
    try {
      const htmlPages = await Promise.all(
        printableVehicles.map(async (row) => {
          const response = await get<ISingleVehicleResponse>(`/vehicle/${row.id}`);
          return generateVehicleLabel(response.data);
        })
      );

      const allHtml = htmlPages.join("<div style='page-break-after: always;'></div>");
      printHtml(allHtml);
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Unable to print vehicle labels",
      });
    } finally {
      setIsPrintingLabels(false);
    }
  };

  const handleClearConfirm = async () => {
    const type = view === "Bays" ? DismantlingBayType.Bay : DismantlingBayType.ProductionLine;
    setIsClearingAll(true);
    try {
      await Promise.all(
        dayColumns.map((date) => clearDay.mutateAsync({ date, dismantlingBayType: type }))
      );
      deleteHandlers.onSuccess();
      setClearOpen(false);
    } catch {
      deleteHandlers.onError();
    } finally {
      setIsClearingAll(false);
    }
  };

  const dayColumns: string[] = useMemo(() => {
    if (view === "Bays") return [fromIso];
    return Array.from({ length: dayRange }, (_, i) =>
      selectedDate.plus({ days: i }).toISODate() ?? ""
    );
  }, [view, dayRange, fromIso, selectedDate]);

  const isLoading = dismantlingBays.isLoading || awaiting.isLoading || allocated.isLoading;

  return (
    <>
      <ConfirmModal
        open={clearOpen}
        setOpen={setClearOpen}
        title="Clear allocations"
        message={`Remove every ${view === "Bays" ? "bay" : "production line"} allocation for ${
          dayColumns.length > 1
            ? `${fromDate.toFormat("ccc d LLL")} - ${toDate.toFormat("ccc d LLL")}`
            : fromDate.toFormat("ccc d LLL")
        }? This cannot be undone.`}
        confirmButtonText="Clear"
        onConfirm={handleClearConfirm}
        isLoading={isClearingAll}
      />

      <div className="mt-2 flex flex-col gap-2 h-[calc(100vh-9rem)]">
        <PlannerToolbar
          view={view}
          setView={setView}
          sort={sort}
          setSort={setSort}
          complexityFilter={complexityFilter}
          setComplexityFilter={setComplexityFilter}
          colourBy={colourBy}
          setColourBy={setColourBy}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          dayRange={dayRange}
          setDayRange={setDayRange}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          bayCount={view === "Bays" ? bayViewBays.length : productionLineBays.length}
          onPrintAll={handlePrintAll}
          isPrintingLabels={isPrintingLabels}
          onClearAll={() => setClearOpen(true)}
          isClearing={isClearingAll}
        />

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center bg-white border border-gray-200 rounded-md">
            <LoadingWheel />
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-12 gap-2 min-h-0">
            <div className="col-span-12 lg:col-span-3 xl:col-span-2 min-h-0">
              <AwaitingDismantlingPanel
                vehicles={awaitingRows}
                isLoading={awaiting.isLoading}
                searchTerm={searchTerm}
                complexityFilter={complexityFilter}
                sort={sort}
                colourBy={colourBy}
                onDropFromBay={handleDropToAwaiting}
              />
            </div>

            <div className="col-span-12 lg:col-span-9 xl:col-span-10 min-h-0">
              <div className="h-full overflow-x-auto overflow-y-hidden">
                <div className="flex gap-2 h-full pr-2">
                  {view === "Bays" ? (
                    bayViewBays.length === 0 ? (
                      <div className="flex-1 bg-white border border-gray-200 rounded-md flex items-center justify-center text-sm text-gray-500">
                        No dismantling bays configured. Add some in Admin Settings.
                      </div>
                    ) : (
                      bayViewBays.map((bay) => (
                        <BayDropZone
                          key={bay.id}
                          bay={bay}
                          date={fromIso}
                          vehicles={vehiclesFor(bay.id, fromIso)}
                          colourBy={colourBy}
                          onDrop={handleDrop}
                          onReorder={handleReorder}
                          variant="column"
                          setting={settingFor(bay.id, fromIso)}
                        />
                      ))
                    )
                  ) : (
                    dayColumns.map((dateIso) => (
                      <ProductionLineDayColumn
                        key={dateIso}
                        date={dateIso}
                        productionLineBays={productionLineBays}
                        vehicles={allocatedRows.filter(
                          (v) => allocationDateKey(v.plannedDismantlingOn) === dateIso
                        )}
                        colourBy={colourBy}
                        onDrop={handleDrop}
                        onReorder={handleReorder}
                        settingFor={(bayId) => settingFor(bayId, dateIso)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
